/**
 * Capture store (zustand + a tiny sessionStorage slice) — forked in shape from
 * vCIP_droom_4.0/src/store/sessionStore.ts, stripped of the V-CIP flow/connection machines.
 *
 * What persists (sessionStorage, tab-scoped): ONLY `linkToken` + `visitId`, so a reload of
 * /capture can re-exchange silently and re-attach to the IndexedDB queue for the same visit. The
 * bearer NEVER persists (api/client.ts holds it in memory). Everything else is re-read from the
 * server on entry — `GET /state` is the truth about what is done; the local `completed` list is a
 * hint that lets the UI advance before the next state read.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { CaptureState, ExchangeSummary, StartResponse } from '@/api/capture';
import type { QueuePersistence } from '@/upload/chunkQueue';

export type SignatureAvailability =
  | { readonly state: 'unknown' }
  | { readonly state: 'available' }
  | { readonly state: 'unavailable'; readonly reason: string };

export interface RefusedShot {
  readonly code: string;
  readonly message: string;
}

export interface SvStoreState {
  linkToken: string | null;
  summary: ExchangeSummary | null;
  bearerExpiresAt: string | null;
  consentRecordedAt: string | null;
  bystanderAcknowledged: boolean;
  visit: StartResponse | null;
  captureState: CaptureState | null;
  /** Shot ids sealed by the server during THIS visit (server-acked). */
  completed: string[];
  /** Shot ids recorded and queued on this device (navigation advances on these; submit waits for `completed`). */
  sealedLocal: string[];
  /** Optional shots the representative chose to skip (navigation advances; submit treats them as absent). */
  skipped: string[];
  /**
   * SPEC_CAPTURE_UX_R2 §7.2: the reason picked when skipping an optional shot — kept on this device and shown on review.
   * The be has no field for it yet (no contract line), so it is never sent; a hint for the representative, not evidence.
   */
  skipReasons: Record<string, string>;
  /** True once POST /geo returned a row id for this visit. */
  geoRecorded: boolean;
  refused: Record<string, RefusedShot>;
  /** Pending chunk count as last reported by the uploader. */
  pendingChunks: number;
  online: boolean;
  persistence: QueuePersistence | null;
  signature: SignatureAvailability;
  receipt: { readonly receiptRef: string; readonly submittedAt: string } | null;
  /** Prompt keys the server said were missing on the last submit attempt. */
  submitMissing: string[];
  /**
   * VC-12 r5: the pack's languages as the OTP CHALLENGE carried them — the only source before the exchange summary
   * exists (the App feeds them to the locale runtime until `summary.packLocales` takes over). Null on India challenges.
   */
  frontDoorPackLocales: readonly string[] | null;
  /**
   * VC-12 r5: a language the customer TAPPED on the code screen (no bearer yet, so it lives on this device only); the
   * front door persists it on the case once the grant exists, so the chooser is not asked again.
   */
  frontDoorLocale: string | null;

  setLinkToken: (t: string) => void;
  setExchange: (summary: ExchangeSummary, expiresAt: string) => void;
  setConsent: (recordedAt: string) => void;
  setBystanderAcknowledged: () => void;
  setVisit: (v: StartResponse) => void;
  setCaptureState: (s: CaptureState) => void;
  markSealedLocal: (shotId: string) => void;
  markSkipped: (shotId: string, reason?: string) => void;
  unskip: (shotIds: readonly string[]) => void;
  setGeoRecorded: (v: boolean) => void;
  unsealLocal: (shotIds: readonly string[]) => void;
  markCompleted: (shotId: string) => void;
  markRefused: (shotId: string, r: RefusedShot) => void;
  clearRefused: (shotId: string) => void;
  setPendingChunks: (n: number) => void;
  setOnline: (v: boolean) => void;
  setPersistence: (p: QueuePersistence) => void;
  setSignature: (s: SignatureAvailability) => void;
  setReceipt: (receiptRef: string) => void;
  setSubmitMissing: (keys: string[]) => void;
  setFrontDoorPackLocales: (locales: readonly string[] | null) => void;
  setFrontDoorLocale: (locale: string | null) => void;
  reset: () => void;
}

const initial = () => ({
  linkToken: null,
  summary: null,
  bearerExpiresAt: null,
  consentRecordedAt: null,
  bystanderAcknowledged: false,
  visit: null,
  captureState: null,
  completed: [] as string[],
  sealedLocal: [] as string[],
  skipped: [] as string[],
  skipReasons: {} as Record<string, string>,
  geoRecorded: false,
  refused: {} as Record<string, RefusedShot>,
  pendingChunks: 0,
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  persistence: null,
  signature: { state: 'unknown' } as SignatureAvailability,
  receipt: null,
  submitMissing: [] as string[],
  frontDoorPackLocales: null as readonly string[] | null,
  frontDoorLocale: null as string | null,
});

export const useCaptureStore = create<SvStoreState>()(
  persist(
    (set) => ({
      ...initial(),
      setLinkToken: (linkToken) => set({ linkToken }),
      setExchange: (summary, bearerExpiresAt) => set({ summary, bearerExpiresAt }),
      setConsent: (consentRecordedAt) => set({ consentRecordedAt }),
      setBystanderAcknowledged: () => set({ bystanderAcknowledged: true }),
      setVisit: (visit) => set({ visit }),
      setCaptureState: (captureState) => set({ captureState }),
      markSkipped: (shotId, reason) =>
        set((s) => ({
          skipped: s.skipped.includes(shotId) ? s.skipped : [...s.skipped, shotId],
          skipReasons: reason ? { ...s.skipReasons, [shotId]: reason } : s.skipReasons,
        })),
      unskip: (shotIds) =>
        set((s) => ({
          skipped: s.skipped.filter((id) => !shotIds.includes(id)),
          skipReasons: Object.fromEntries(Object.entries(s.skipReasons).filter(([id]) => !shotIds.includes(id))),
        })),
      setGeoRecorded: (geoRecorded) => set({ geoRecorded }),
      markSealedLocal: (shotId) =>
        set((s) => ({ sealedLocal: s.sealedLocal.includes(shotId) ? s.sealedLocal : [...s.sealedLocal, shotId] })),
      unsealLocal: (shotIds) =>
        set((s) => ({
          sealedLocal: s.sealedLocal.filter((id) => !shotIds.includes(id)),
          completed: s.completed.filter((id) => !shotIds.includes(id)),
        })),
      markCompleted: (shotId) =>
        set((s) => {
          const { [shotId]: _dropped, ...refused } = s.refused;
          return {
            completed: s.completed.includes(shotId) ? s.completed : [...s.completed, shotId],
            refused,
          };
        }),
      markRefused: (shotId, r) => set((s) => ({ refused: { ...s.refused, [shotId]: r } })),
      clearRefused: (shotId) =>
        set((s) => {
          const { [shotId]: _dropped, ...refused } = s.refused;
          return { refused };
        }),
      setPendingChunks: (pendingChunks) => set({ pendingChunks }),
      setOnline: (online) => set({ online }),
      setPersistence: (persistence) => set({ persistence }),
      setSignature: (signature) => set({ signature }),
      setReceipt: (receiptRef) => set({ receipt: { receiptRef, submittedAt: new Date().toISOString() } }),
      setSubmitMissing: (submitMissing) => set({ submitMissing }),
      setFrontDoorPackLocales: (frontDoorPackLocales) => set({ frontDoorPackLocales }),
      setFrontDoorLocale: (frontDoorLocale) => set({ frontDoorLocale }),
      reset: () => set(initial()),
    }),
    {
      name: 'vcip-svapp',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ linkToken: s.linkToken, visitId: s.visit?.visitId ?? null }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as { linkToken?: string | null };
        return { ...current, linkToken: p.linkToken ?? current.linkToken };
      },
    },
  ),
);
